import { Select, Space, Spin } from "antd";
import { Resource } from "dtos/stage.dto";
import { T_MongoId } from "types/_utils.types";
import { ApiService } from "api/api.service";
import { useMemo } from "react";
import { resolveStageColor, resolveStageIcon } from "components/Stages/constants";

interface TaskStagePickerProps {
  /** Organisation code — used to fetch stages */
  orgCode: string | undefined;
  /** The resource type to filter stages by */
  resource: Resource | undefined;
  /** Currently selected stage ObjectId */
  value?: T_MongoId;
  onChange?: (stageId: T_MongoId | undefined) => void;
  disabled?: boolean;
}

/**
 * A stage picker scoped to a specific resource.
 * Unlike ActionPicker (which outputs "resource:value" for kanban columns),
 * this returns the stage's ObjectId — suitable for task item's `relatedStage` field.
 */
export const TaskStagePicker = ({ orgCode, resource, value, onChange, disabled }: TaskStagePickerProps) => {
  const stagesQuery = ApiService.organisation.useGetAllStages(orgCode);

  const options = useMemo(() => {
    if (!stagesQuery.data || !resource) return [];

    return stagesQuery.data
      .filter((s) => s.resource === resource && !s.archived)
      .map((stage) => {
        const Icon = resolveStageIcon(stage.icon);
        const swatch = resolveStageColor(stage.color);

        return {
          value: stage._id,
          label: (
            <Space size={6}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  background: swatch.bg,
                  color: swatch.text,
                  boxShadow: `inset 0 0 0 1px ${swatch.border}`,
                  flexShrink: 0,
                }}
              >
                <Icon size={12} />
              </span>
              <span>{stage.label}</span>
            </Space>
          ),
        };
      });
  }, [stagesQuery.data, resource]);

  if (stagesQuery.isLoading) return <Spin size="small" />;

  return (
    <Select
      allowClear
      placeholder="Link to a stage (optional)"
      style={{ width: "100%" }}
      value={value ? String(value) : undefined}
      onChange={(val) => onChange?.(val ?? undefined)}
      options={options}
      disabled={disabled || !resource}
      notFoundContent={
        !resource ? "No resource selected" : stagesQuery.isError ? "Failed to load stages" : "No stages found"
      }
    />
  );
};
